import React, { useMemo } from "react"
import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormRegister, UseFormTrigger, UseFormWatch } from "react-hook-form";
import moment from "moment";
import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { integerRegex } from "../../../../shared/validators";
import { type ChangeMachine, type Machine, type CustomerMachineryState } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getReferenceLabel, sortReferenceRows } from "../../../../shared/utils/machineReferenceLabels";

export interface CncFormProps {
  machine?: Machine;
  control: Control<ChangeMachine>;
  register: UseFormRegister<ChangeMachine>;
  errors: FieldErrors<ChangeMachine>;
  watch: UseFormWatch<ChangeMachine>;
  trigger: UseFormTrigger<ChangeMachine>;
  clearErrors: UseFormClearErrors<ChangeMachine>;
}

const CncForm = ({ machine, control, register, errors, watch, trigger, clearErrors }: CncFormProps) => {
  const { manufacturers, operationTypes, controlSystemBrands, controlSystemModels, axisConfigurations } = useAppSelector<CustomerMachineryState>(
    (state: any) => state.customerMachinery
  );
  const { translate } = useTranslation("myMachinery");
  const controlSystemBrandId = watch("controlSystemBrandId");
  const { translate: tCommon } = useTranslation("common");
  // MACH-REF-I18N-1: labels translated by reference code, lists sorted by the localised label
  // ("Other" last). Values are unchanged: row ids, and the English NAME for the axis field.
  const sortedOperationTypes = useMemo(() => sortReferenceRows(operationTypes || [], "operation", tCommon), [operationTypes, tCommon]);
  const sortedAxisConfigurations = useMemo(() => sortReferenceRows(axisConfigurations || [], "axis", tCommon), [axisConfigurations, tCommon]);

  const stylesPlaceholder = {
    "input": {
      "&::placeholder": {
        opacity: 1
      }
    }
  };

  const manufacturerId = watch("manufacturerId");

  return (
    <Box>
      <Box className="mt-5">
        <InputLabel required>{tCommon("brand")}</InputLabel>
        <Controller
          name="manufacturerId"
          control={control}
          defaultValue=""
          rules={{ required: tCommon("forms.required") }}
          render={({ field }) => (
            <Autocomplete
              options={manufacturers}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={manufacturers.find(m => m.id === field.value) || null}
              onChange={(_, selected) => {
                field.onChange(selected?.id || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors.manufacturerId}
                  helperText={errors.manufacturerId?.message}
                  placeholder={translate("chooseFromList")}
                />
              )}
            />
          )}
        />
      </Box>

      {manufacturerId && manufacturers.find(m => m.id === manufacturerId)?.name === "Other" && (
        <Box className="mt-5">
          <InputLabel required>{tCommon("brand")}</InputLabel>
          <TextField
            className="w-full"
            defaultValue={machine?.customBrandName || ""}
            {...register("customBrandName", { required: tCommon("forms.required") })}
            inputProps={{ maxLength: 50 }}
            error={!!errors.customBrandName}
            helperText={errors.customBrandName?.message}
          ></TextField>
        </Box>
      )}

      <Box className="mt-5">
        <InputLabel required>{translate("model")}</InputLabel>
        <TextField
          className="w-full"
          defaultValue={machine?.model || ""}
          {...register("model", { required: tCommon("forms.required") })}
          inputProps={{ maxLength: 50 }}
          placeholder={translate("modelPlaceholder")}
          error={!!errors.model}
          helperText={errors.model?.message}
        ></TextField>
      </Box>

      <Box className="mt-5">
        <InputLabel required>{translate("operationType")}</InputLabel>
        <FormControl className="w-full">
          <Select
            displayEmpty
            defaultValue={machine?.operationType?.id || ""}
            onClose={() => setTimeout(() => trigger("operationTypeId"))}
            {...register("operationTypeId", { required: tCommon("forms.required") })}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 250,
                  overflow: 'auto'
                }
              }
            }}
            renderValue={(selected) => {
              if (!selected) {
                return <Box>{translate("chooseFromList")}</Box>;
              }

              return getReferenceLabel("operation", operationTypes.find((x) => x.id === selected), tCommon);
            }}
            error={!!errors.operationTypeId}
          >
            {sortedOperationTypes.map((operationType) => (
              <MenuItem key={operationType.id} value={operationType.id}>
                {getReferenceLabel("operation", operationType, tCommon)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className="mt-5">
        <InputLabel required>{translate("axisConfiguration")}</InputLabel>
        {/* MACH-REF-I18N-1: this field used to register the Autocomplete's TEXT INPUT, so the
            visible label WAS the submitted value (MachineDialog resolves it with
            `axisConfigurations.find(x => x.name === value)`). Now that the label is translated
            (and two rows can share one, axis-4 / axis-4-rare -> "4 osi"), a Controller stores the
            selected row's English NAME instead — the same value as before, whatever the language.
            The edit reset writes the row ID into this field, so the value lookup accepts both. */}
        <Controller
          name="axisConfigurationId"
          control={control}
          rules={{ required: true }}
          defaultValue={machine?.axisConfiguration?.name}
          render={({ field }) => (
            <Autocomplete
              options={sortedAxisConfigurations}
              getOptionLabel={(option) => getReferenceLabel("axis", option, tCommon)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={axisConfigurations.find((x) => x.name === field.value || x.id === field.value) || null}
              ListboxProps={{ style: { maxHeight: "20vh" } }}
              onChange={(_, option) => {
                field.onChange(option?.name ?? "");
                clearErrors();
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  {getReferenceLabel("axis", option, tCommon)}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  sx={stylesPlaceholder}
                  placeholder={translate("chooseFromList")}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  error={!!errors.axisConfigurationId}
                  helperText={errors.axisConfigurationId ? tCommon("forms.required") : ""}
                />
              )}
            />
          )}
        />
      </Box>

      <Box className="grid grid-cols-2 gap-4 mt-5">
        <Box>
          <InputLabel required>{translate("serialNumber")}</InputLabel>
          <TextField
            className="w-full"
            defaultValue={machine?.serialNumber || ""}
            placeholder={translate("serialNumberPlaceholder")}
            {...register("serialNumber", { required: tCommon("forms.required") })}
            inputProps={{ maxLength: 50 }}
            error={!!errors.serialNumber}
            helperText={errors.serialNumber?.message}
          ></TextField>
        </Box>

        <Box>
          <InputLabel>{translate("productionYear")}</InputLabel>
          <TextField
            className="w-full"
            placeholder={moment().year().toString()}
            {...register("productionYear", {
              pattern: { value: integerRegex, message: translate("fieldRequire4Digits") },
              min: { value: 1900, message: translate("enterValidYearRange", { year: moment().year() }) },
              max: { value: moment().year(), message: translate("enterValidYearRange", { year: moment().year() }) }
            })}
            inputProps={{ maxLength: 4 }}
            error={!!errors.productionYear}
            helperText={errors.productionYear?.message}
          />
        </Box>

        <Box>
          <InputLabel required>{translate("controlSystemBrand")}</InputLabel>
          <FormControl className="w-full">
            <Select
              displayEmpty
              defaultValue={machine?.controlSystemBrand?.id || ""}
              onClose={() => setTimeout(() => trigger("controlSystemBrandId"))}
              {...register("controlSystemBrandId", { required: tCommon("forms.required") })}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                PaperProps: {
                  style: {
                    maxHeight: 180,
                    overflow: 'auto'
                  }
                }
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return <Box>{translate("chooseFromList")}</Box>;
                }

                return getReferenceLabel("brand", controlSystemBrands.find((x) => x.id === selected), tCommon);
              }}
              error={!!errors.controlSystemBrandId}
            >
              {controlSystemBrands?.map((controlSystemBrand) => (
                <MenuItem key={controlSystemBrand.id} value={controlSystemBrand.id}>
                  {getReferenceLabel("brand", controlSystemBrand, tCommon)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <InputLabel required>{translate("controlSystemModel")}</InputLabel>
          <Autocomplete
            freeSolo
            disabled={controlSystemBrandId === ""}
            options={controlSystemModels.map((m) => m.name)}
            getOptionLabel={(option) => option}
            defaultValue={machine?.system}
            ListboxProps={{ style: { maxHeight: "20vh" } }}
            onChange={() => clearErrors()}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                sx={stylesPlaceholder}
                placeholder={translate("typeOrChoose")}
                inputProps={{
                  ...params.inputProps
                }}
                {...register("system", { required: true })}
                error={!!errors.system}
                helperText={errors.system ? tCommon("forms.required") : ""}
              />
            )}
          />
        </Box>
      </Box>
    </Box>);
};

export default CncForm;